// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import { loginAction, authentication } from "../../authenticationSlice";
// Formik Framework:
import { useFormik } from "formik";
// Yup for validation:
import * as yup from "yup";
// MUI Components:
import {
  Box,
  TextField,
  Button,
  SvgIcon,
  InputAdornment,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// MUI Icones:
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// Icones:
import { ReactComponent as UserIcon } from "../../../../assets/icons/user-icon.svg";
import { ReactComponent as KeyIcon } from "../../../../assets/icons/key-icon.svg";
import { ReactComponent as AlertIcon } from "../../../../assets/icons/alert-icon.svg";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/check-circle-icon.svg";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "280px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      width: "80%",
    },
    [theme.breakpoints.down(460)]: {
      width: "100%",
    },
  },
  field: {
    borderRadius: "5px",
    backgroundColor: theme.palette.primary.main,
    "& label.Mui-focused": {
      color: theme.palette.text.primary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1.5,
      },
    },
  },
}));

const validationSchema = yup.object({
  username: yup.string().required("Please enter your username."),
  password: yup
    .string()
    // .matches(
    //   /^(?=.*[A-Za-z])(?=.*[\d$@.!%*#$?&])[A-Za-z\d$@.!%*#$?&]/,
    //   "Must be only digits"
    // )
    // .min(8, "Password must have more than 8 characters.")
    .required("Please enter your password."),
});

export const LoginForm = () => {
  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const {
    token,
    actionStatus,
    alert: { message },
  } = useAppSelector(authentication);

  const [authStatus, setAuthStatus] = useState({ message: "", loading: false });
  const [showPass, setShowPass] = useState({
    password: false,
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // alert(JSON.stringify(values, null, 2));
      dispatch(loginAction(values));
    },
  });

  useEffect(() => {
    if (actionStatus === "loading") {
      setAuthStatus({ message: actionStatus, loading: true });
    }

    if (!!token && actionStatus === "success") {
      setAuthStatus({ message: actionStatus, loading: true });
    }

    if (actionStatus === "rejected") {
      setTimeout(() => {
        setAuthStatus({ message: actionStatus, loading: false });
      }, 500);
    }
  }, [token, actionStatus, message]);

  useEffect(() => {
    setAuthStatus({ message: "", loading: false });
  }, [formik.values.username, formik.values.password]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        [theme.breakpoints.down("sm")]: {
          alignItems: "center",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        className={classes.form}
      >
        <Box sx={{ width: "100%", padding: "14px 0px" }}>
          <Typography
            sx={{
              fontSize: "28px",
              color: theme.palette.text.primary,
              [theme.breakpoints.down("md")]: {
                fontSize: "24px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "22px",
              },
            }}
          >
            Login
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "flex-end",
            padding: "8px 14px",
          }}
        >
          <SvgIcon
            component={UserIcon}
            inheritViewBox
            sx={{
              width: "18px",
              color: theme.palette.text.primary,
              mr: "10px",
            }}
          />
          <Typography
            sx={{
              lineHeight: "1",
              color: theme.palette.text.primary,
            }}
          >
            User Name
          </Typography>
        </Box>
        <TextField
          name="username"
          placeholder="User Name"
          type="text"
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.username && Boolean(formik.errors.username)}
          size={"small"}
          sx={{
            label: { color: theme.palette.text.primary },
            input: {
              padding: "14px",
              color: theme.palette.text.primary,
              "&::placeholder": {
                color: theme.palette.text.primary,
                opacity: "0.5",
              },
              "&:-webkit-autofill": {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.primary.main} inset`,
                WebkitTextFillColor: theme.palette.text.primary,
                transition: " background-color 600000s 0s, color 600000s 0s",
              },
              [theme.breakpoints.down("sm")]: {
                padding: "12px",
              },
            },
            mb: "12px",
            width: "100%",
          }}
          className={classes.field}
        />

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "flex-end",
            padding: "8px 14px",
          }}
        >
          <SvgIcon
            component={KeyIcon}
            inheritViewBox
            sx={{
              width: "22px",
              height: "18px",
              color: theme.palette.text.primary,
              mr: "10px",
            }}
          />
          <Typography
            sx={{
              lineHeight: "1",
              color: theme.palette.text.primary,
            }}
          >
            Password
          </Typography>
        </Box>
        <TextField
          name="password"
          placeholder="Passsword"
          type={showPass.password ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          size={"small"}
          sx={{
            label: { color: theme.palette.text.primary },
            input: {
              padding: "14px",
              paddingLeft: "0px",
              color: theme.palette.text.primary,
              "&::placeholder": {
                color: theme.palette.text.primary,
                opacity: "0.5",
              },
              "&:-webkit-autofill": {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.primary.main} inset`,
                WebkitTextFillColor: theme.palette.text.primary,
                transition: " background-color 600000s 0s, color 600000s 0s",
              },
              [theme.breakpoints.down("sm")]: {
                padding: "12px",
              },
            },
            mb: 3,
            width: "100%",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{ marginLeft: "14px", marginRight: "10px" }}
              >
                <SvgIcon
                  component={
                    showPass.password ? VisibilityOffIcon : VisibilityIcon
                  }
                  inheritViewBox
                  sx={{
                    width: "22px",
                    cursor: "pointer",
                    color: theme.palette.text.primary,
                    opacity: 0.5,
                  }}
                  onClick={() => {
                    setShowPass((last) => ({
                      ...last,
                      password: !last.password,
                    }));
                  }}
                />
              </InputAdornment>
            ),
          }}
          className={classes.field}
        />

        <Box sx={{ position: "relative" }}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            type="submit"
            disabled={authStatus.loading}
            sx={{
              width: "140px",
              fontSize: "16px",
              borderRadius: "8px",
              textTransform: "none",
              lineHeight: "0px",
              padding: "20px",
              transition: "all 0.4s",
              ":disabled": {
                color: theme.palette.primary.contrastText,
                boxShadow: "none",
                backgroundColor: theme.palette.secondary.main,
                opacity: "0.3",
              },
            }}
          >
            Login
          </Button>
          {authStatus.loading && (
            <CircularProgress
              size={24}
              sx={{
                color: (theme) => theme.palette.blue?.dark,
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>
        <Box sx={{ m: "16px 0px 0px 0px;" }}>
          <Typography
            sx={{ color: theme.palette.text.primary, fontSize: "14px" }}
          >
            {process.env.REACT_APP_VERSION}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        {formik.touched.username && Boolean(formik.errors.username) && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <SvgIcon
              component={AlertIcon}
              inheritViewBox
              sx={{
                width: "22px",
                color: "#eaf63c",
                mr: "10px",
                [theme.breakpoints.down("sm")]: {
                  width: "18px",
                },
              }}
            />
            <Typography
              sx={{
                width: "300px",
                lineHeight: "1.2",
                color: "#EAF63C",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }}
            >
              Please enter your username
            </Typography>
          </Box>
        )}

        {formik.touched.password && Boolean(formik.errors.password) && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <SvgIcon
              component={AlertIcon}
              inheritViewBox
              sx={{
                width: "22px",
                color: "#eaf63c",
                mr: "10px",
                [theme.breakpoints.down("sm")]: {
                  width: "18px",
                },
              }}
            />
            <Typography
              sx={{
                width: "300px",
                lineHeight: "1.2",
                color: "#EAF63C",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }}
            >
              {/* Password must be at least 8 characters and it must contain at
              least one number or special character. */}
              Please enter your password
            </Typography>
          </Box>
        )}

        {authStatus.message === "success" && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <SvgIcon
              component={CheckIcon}
              inheritViewBox
              sx={{
                width: "22px",
                color: "#66E66F",
                mr: "10px",
                [theme.breakpoints.down("sm")]: {
                  width: "18px",
                },
              }}
            />
            <Typography
              sx={{
                width: "300px",
                lineHeight: "1.2",
                color: "#66E66F",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }}
            >
              Welcome{" "}
              <span style={{ color: "#cad531" }}>{formik.values.username}</span>
              , please wait.
            </Typography>
          </Box>
        )}

        {authStatus.message === "rejected" && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <SvgIcon
              component={AlertIcon}
              inheritViewBox
              sx={{
                width: "22px",
                color: "#E36464",
                mr: "10px",
                [theme.breakpoints.down("sm")]: {
                  width: "18px",
                },
              }}
            />
            <Typography
              sx={{
                width: "300px",
                lineHeight: "1.2",
                color: "#E36464",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }}
            >
              {message === "notMatch"
                ? "Username or password is wrong."
                : "Unknown error happend"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoginForm;
